import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutList, LayoutGrid, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ActivityListView } from './ActivityListView';
import { ActivityVisualView } from './ActivityVisualView';
import { activityService } from '@/lib/api/activity.service';
import type { Activity, ReorderActivitiesRequest } from '@/types/activity.types';

type ViewMode = 'list' | 'visual';
const VIEW_MODE_KEY = 'activities_view_mode';

interface ActivitiesManagerProps {
  eventId: number;
  canManage: boolean;
  onActivitiesLoaded?: (count: number) => void;
}

interface ErrorResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

export const ActivitiesManager: React.FC<ActivitiesManagerProps> = ({ eventId, canManage, onActivitiesLoaded }) => {
  const { t } = useTranslation('activities');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) || 'list';
  });
  const onActivitiesLoadedRef = useRef(onActivitiesLoaded);

  useEffect(() => {
    onActivitiesLoadedRef.current = onActivitiesLoaded;
  }, [onActivitiesLoaded]);

  // ─── Data loading ──────────────────────────────────────────────────────────

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await activityService.getActivitiesByEvent(eventId, 'sequence');
      setActivities(data);
      onActivitiesLoadedRef.current?.(data.length);
    } catch (err: unknown) {
      const message = (err as ErrorResponse)?.response?.data?.message || t('manager.errors.loadActivities');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // ─── View mode toggle ──────────────────────────────────────────────────────

  const handleViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  // ─── Activity operations ───────────────────────────────────────────────────

  const handleAdd = async (title: string) => {
    const created = await activityService.createActivity(eventId, { title });
    setActivities((prev) => [...prev, created]);
  };

  const handleAddParallel = async (parallelGroup: string, title: string) => {
    const created = await activityService.createActivity(eventId, { title, parallel_group: parallelGroup });
    setActivities((prev) => [...prev, created]);
  };

  const handleUpdated = (updated: Activity) => {
    setActivities((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };

  const handleDeleted = async (id: number) => {
    const remaining = activities
      .filter((a) => a.id !== id)
      .map((a, idx) => ({ ...a, sequence_order: idx + 1 }));

    setActivities(remaining);

    if (remaining.length > 0) {
      try {
        await activityService.reorderActivities(eventId, {
          activities: remaining.map((a) => ({
            id: a.id,
            sequence_order: a.sequence_order,
            parallel_group: a.parallel_group ?? null,
          })),
        });
      } catch {
        loadActivities(); // rollback on error
      }
    }
  };

  const handleReorder = async (reordered: Activity[]) => {
    // Optimistic update
    setActivities(reordered);
    try {
      const reorderData: ReorderActivitiesRequest = {
        activities: reordered.map((a) => ({
          id: a.id,
          sequence_order: a.sequence_order,
          parallel_group: a.parallel_group ?? null,
        })),
      };
      await activityService.reorderActivities(eventId, reorderData);
    } catch {
      // Rollback on error by reloading
      loadActivities();
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        <span className="ml-3 text-gray-600">{t('manager.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">{t('title')}</h2>
          <span className="text-sm text-gray-400">({activities.length})</span>
        </div>

        {/* View toggle */}
        <div className="flex items-center border rounded-lg overflow-hidden">
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-none h-8 px-3"
            onClick={() => handleViewMode('list')}
            title={t('manager.listView')}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'visual' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-none h-8 px-3 border-l"
            onClick={() => handleViewMode('visual')}
            title={t('manager.visualView')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Views */}
      {viewMode === 'list' ? (
        <ActivityListView
          activities={activities}
          eventId={eventId}
          canManage={canManage}
          onAdded={handleAdd}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
          onReorder={handleReorder}
        />
      ) : (
        <ActivityVisualView
          activities={activities}
          eventId={eventId}
          canManage={canManage}
          onAdded={handleAdd}
          onAddParallel={handleAddParallel}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};
